import * as _ from 'lodash';

import { ValueTypes } from '../../common/ValueTypes';
import { ActualDataType, AggregationMode, Dictionary, DigitalOrStringConvertibleValue, IValueViewModel, TagDateValue, TagStringValueDto, WidgetValues } from '../../models';
import { RowViewModel } from 'widgets';
import { guidEmpty } from '../../common/constants';

export interface IStringValuesHelperService {
    getStringValuesId(values: TagDateValue[]);
    getStringValuesIdForString(values: TagDateValue[], rowViewModels: RowViewModel[]);
    getValueOrString(vm: IValueViewModel, value: DigitalOrStringConvertibleValue, stringValues: TagStringValueDto[], aggregationMode: AggregationMode): number | string;
    isConvertibleToStringValue(valueType: string): boolean;
    mapStringForTagDatesValues(values: TagDateValue[], stringValues: TagStringValueDto[], viewModels: Dictionary<IValueViewModel>, aggregationMode: AggregationMode): TagDateValue[];
    mapStringForTagDatesValuesForOne(values: TagDateValue[], stringValues: TagStringValueDto[], viewModel: IValueViewModel, aggregationMode: AggregationMode): TagDateValue[]
    mapStringValues(widgetValues: WidgetValues[], stringValues: TagStringValueDto[], viewModels: Dictionary<IValueViewModel>, aggregationMode: AggregationMode): WidgetValues[];
}

export class StringValuesHelperService implements IStringValuesHelperService {
    static serviceId = 'stringValuesHelperService';

    static $inject = [];

    getValueOrString(
        vm: IValueViewModel,
        value: TagDateValue,
        stringValues: TagStringValueDto[],
        aggregationMode: AggregationMode
    ): number | string {
        let result: string;

        if (vm) {
            const tagGuid = vm.tagGuid && vm.tagGuid !== guidEmpty ? vm.tagGuid : vm.guid;
            result = this.getValue(value, stringValues, vm.actualDataType, aggregationMode, tagGuid);
        }

        return result ?? value.value;
    }

    isConvertibleToStringValue(valueType: string): boolean {
        if (!valueType) {
            return false;
        }
        const vt = valueType.toLowerCase();
        return (ValueTypes.ValueTypesSupportedByStringDataType.indexOf(vt) >= 0);
    }

    mapStringValues(widgetValues: WidgetValues[], stringValues: TagStringValueDto[], viewModels: Dictionary<IValueViewModel>, aggregationMode: AggregationMode): WidgetValues[] {
        if (widgetValues?.length > 0 && stringValues?.length > 0 && viewModels && Object.keys(viewModels).length > 0) {
            widgetValues.forEach(wv => {
                wv.aggregatedValue = this.mapStringForTagDatesValues(wv.aggregatedValue, stringValues, viewModels, aggregationMode);
                wv.aggregatedValues = this.mapStringForTagDatesValues(wv.aggregatedValues, stringValues, viewModels, aggregationMode);
                wv.values = this.mapStringForTagDatesValues(wv.values, stringValues, viewModels, aggregationMode);
            });
        }
        return widgetValues;
    }

    mapStringForTagDatesValues(
        values: TagDateValue[],
        stringValues: TagStringValueDto[],
        viewModels: Dictionary<IValueViewModel>,
        aggregationMode: AggregationMode
    ): TagDateValue[] {
        if (values && values.length > 0) {
            values
                .filter(v => this.isConvertibleToStringValue(v.valueType) && !v.comment)
                .forEach(v => v.comment = this.getValueFromViewModel(v, stringValues, viewModels, aggregationMode));
        }
        return values;
    }

    mapStringForTagDatesValuesForOne(
        values: TagDateValue[],
        stringValues: TagStringValueDto[],
        viewModel: IValueViewModel,
        aggregationMode: AggregationMode
    ): TagDateValue[] {
        if (values?.length > 0) {
            values
                .filter(v => this.isConvertibleToStringValue(v.valueType) && !v.comment)
                .forEach(v => v.comment = this.getValueFromViewModelForOne(v, stringValues, viewModel, aggregationMode));
        }
        return values;
    }

    getStringValuesId(values: TagDateValue[]) {
        let stringValuesId: number[] = [];
        if (values) {
            stringValuesId = values.map(value => value.value);
        }
        return _.uniq(stringValuesId);
    }

    getStringValuesIdForString(values: TagDateValue[], rowViewModels: RowViewModel[]) {
        let stringValuesId: number[] = [];
        if (rowViewModels && values) {
            const onlyString = _.uniq(rowViewModels.filter(vm => vm.dataType === ActualDataType.String).map(vm => vm.guid));
            stringValuesId = values.filter(value => onlyString.includes(value.guid)).map(value => value.value);
        }
        return _.uniq(stringValuesId);
    }

    private getValueFromViewModel(
        value: TagDateValue,
        stringValues: TagStringValueDto[],
        viewModels: Dictionary<IValueViewModel>,
        aggregationMode: AggregationMode
    ): string {
        const foundVm = viewModels[value.guid];
        return this.getValueFromViewModelForOne(value, stringValues, foundVm, aggregationMode);
    }

    private getValueFromViewModelForOne(
        value: TagDateValue,
        stringValues: TagStringValueDto[],
        viewModel: IValueViewModel,
        aggregationMode: AggregationMode
    ): string {
        let result = null;
        if (viewModel) {
            const tagGuid = viewModel.tagGuid && viewModel.tagGuid !== guidEmpty ? viewModel.tagGuid : viewModel.guid;
            result = this.getValue(value, stringValues, viewModel.actualDataType, aggregationMode, tagGuid);
        }
        return result ?? value.comment;
    }

    private getValue(
        value: DigitalOrStringConvertibleValue,
        stringValues: TagStringValueDto[],
        actualDataType: ActualDataType,
        aggregationMode: AggregationMode,
        tagGuid: string
    ): string {
        const foundString = stringValues?.length > 0
            ? stringValues.find(tsv => tsv.id === value.value && tsv.tagGuid === tagGuid)
            : undefined;
        if (value.value !== undefined
            && actualDataType
            && actualDataType === ActualDataType.String
            && stringValues
            && foundString
            && this.isConvertibleToStringValue(value.valueType)
        ) {
            let result = foundString.stringValue;
            if (aggregationMode === AggregationMode.Average && value.count !== undefined) {
                const percentage = Math.round(value.count * 100);
                result = value.count === 1
                    ? `${result}`
                    : `${result} (${percentage}%)`;
            }
            return result;
        }
    }
}